import { createContext, useState, useRef, useCallback, useEffect, useContext, useMemo } from 'react';
import { initializeApiClient } from '@respectly/api-client';
import { jsx, Fragment, jsxs } from 'react/jsx-runtime';

// src/auth/AuthProvider.tsx

// src/recaptcha.ts
var SCRIPT_ATTR = "data-respectly-recaptcha";
var scriptPromise = null;
var warnedMissingKey = false;
function getSiteKey() {
  if (typeof import.meta === "undefined" || !import.meta.env) return void 0;
  const key = import.meta.env.VITE_RECAPTCHA_SITE_KEY;
  return typeof key === "string" && key.length > 0 ? key : void 0;
}
function loadScript(siteKey) {
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise((resolve, reject) => {
    if (typeof document === "undefined") {
      reject(new Error("recaptcha: not in a browser environment"));
      return;
    }
    const existing = document.querySelector(`script[${SCRIPT_ATTR}]`);
    if (existing) {
      if (window.grecaptcha?.enterprise) {
        resolve();
      } else {
        existing.addEventListener("load", () => resolve(), { once: true });
        existing.addEventListener("error", () => reject(new Error("recaptcha: script failed to load")), { once: true });
      }
      return;
    }
    const script = document.createElement("script");
    script.src = `https://www.google.com/recaptcha/enterprise.js?render=${encodeURIComponent(siteKey)}`;
    script.async = true;
    script.defer = true;
    script.setAttribute(SCRIPT_ATTR, "");
    script.addEventListener("load", () => resolve(), { once: true });
    script.addEventListener("error", () => reject(new Error("recaptcha: script failed to load")), { once: true });
    document.head.appendChild(script);
  });
  return scriptPromise;
}
async function executeRecaptcha(action) {
  const siteKey = getSiteKey();
  if (!siteKey) {
    if (!warnedMissingKey) {
      warnedMissingKey = true;
      console.warn("[recaptcha] VITE_RECAPTCHA_SITE_KEY not set \u2014 sending empty tokens");
    }
    return "";
  }
  try {
    await loadScript(siteKey);
    const grecaptcha = window.grecaptcha;
    if (!grecaptcha?.enterprise) {
      console.warn("[recaptcha] grecaptcha.enterprise not available after script load");
      return "";
    }
    return await new Promise((resolve) => {
      grecaptcha.enterprise.ready(() => {
        grecaptcha.enterprise.execute(siteKey, { action }).then(resolve).catch((err) => {
          console.warn("[recaptcha] execute failed:", err);
          resolve("");
        });
      });
    });
  } catch (err) {
    console.warn("[recaptcha] token generation failed:", err);
    return "";
  }
}
var DEFAULT_API_URL = typeof import.meta !== "undefined" && import.meta.env?.VITE_API_URL || "";
function isTrialActive(tenant) {
  if (!tenant || tenant.trialStatus !== "trialing" || !tenant.trialEndsAt) {
    return false;
  }
  return new Date(tenant.trialEndsAt) > /* @__PURE__ */ new Date();
}
var AuthContext = createContext(null);
var ACCESS_TOKEN_KEY = "respectly_access_token";
var REFRESH_TOKEN_KEY = "respectly_refresh_token";
var LEGACY_ACCESS_TOKEN_KEY = "nexus_access_token";
var LEGACY_REFRESH_TOKEN_KEY = "nexus_refresh_token";
function migrateLegacyTokens() {
  const legacyAccess = localStorage.getItem(LEGACY_ACCESS_TOKEN_KEY);
  const legacyRefresh = localStorage.getItem(LEGACY_REFRESH_TOKEN_KEY);
  if (legacyAccess && !localStorage.getItem(ACCESS_TOKEN_KEY)) {
    localStorage.setItem(ACCESS_TOKEN_KEY, legacyAccess);
  }
  if (legacyRefresh && !localStorage.getItem(REFRESH_TOKEN_KEY)) {
    localStorage.setItem(REFRESH_TOKEN_KEY, legacyRefresh);
  }
  localStorage.removeItem(LEGACY_ACCESS_TOKEN_KEY);
  localStorage.removeItem(LEGACY_REFRESH_TOKEN_KEY);
}
function storeTokens(accessToken, refreshToken) {
  localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
  localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
}
function getStoredTokens() {
  return {
    accessToken: localStorage.getItem(ACCESS_TOKEN_KEY),
    refreshToken: localStorage.getItem(REFRESH_TOKEN_KEY)
  };
}
function clearTokens() {
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
}
function isTokenExpired(token) {
  try {
    const parts = token.split(".");
    if (parts.length !== 3 || !parts[1]) {
      return true;
    }
    const payload = JSON.parse(atob(parts[1]));
    const exp = payload.exp * 1e3;
    return Date.now() >= exp - 3e4;
  } catch {
    return true;
  }
}
function AuthProvider({
  children,
  apiUrl = DEFAULT_API_URL,
  authPathPrefix = "/api/auth",
  loadingComponent
}) {
  const [user, setUser] = useState(null);
  const [tenant, setTenant] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);
  const [error, setError] = useState(null);
  const accessTokenRef = useRef(null);
  const refreshPromiseRef = useRef(null);
  const apiRequest = useCallback(
    async (endpoint, options = {}) => {
      const url = `${apiUrl}${endpoint}`;
      const headers = {
        "Content-Type": "application/json",
        ...options.headers
      };
      if (accessTokenRef.current) {
        headers["Authorization"] = `Bearer ${accessTokenRef.current}`;
      }
      const response = await fetch(url, {
        ...options,
        headers
      });
      return response;
    },
    [apiUrl]
  );
  const refreshToken = useCallback(async () => {
    if (refreshPromiseRef.current) {
      return refreshPromiseRef.current;
    }
    const tokens = getStoredTokens();
    if (!tokens.refreshToken) {
      return false;
    }
    refreshPromiseRef.current = (async () => {
      try {
        const response = await fetch(`${apiUrl}${authPathPrefix}/refresh`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ refreshToken: tokens.refreshToken })
        });
        if (!response.ok) {
          clearTokens();
          setUser(null);
          setTenant(null);
          accessTokenRef.current = null;
          return false;
        }
        const data = await response.json();
        if (data.success) {
          accessTokenRef.current = data.data.accessToken;
          storeTokens(data.data.accessToken, data.data.refreshToken);
          return true;
        }
        return false;
      } catch {
        return false;
      } finally {
        refreshPromiseRef.current = null;
      }
    })();
    return refreshPromiseRef.current;
  }, [apiUrl, authPathPrefix]);
  const getAccessToken = useCallback(async () => {
    if (accessTokenRef.current && !isTokenExpired(accessTokenRef.current)) {
      return accessTokenRef.current;
    }
    const success = await refreshToken();
    return success ? accessTokenRef.current : null;
  }, [refreshToken]);
  const fetchUserProfile = useCallback(async () => {
    try {
      const response = await apiRequest(`${authPathPrefix}/me`);
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setUser({
            id: data.data.user.id,
            email: data.data.user.email,
            firstName: data.data.user.firstName,
            lastName: data.data.user.lastName,
            role: data.data.user.role,
            tenantId: data.data.tenant.id,
            emailVerified: data.data.user.emailVerified,
            isOwner: data.data.user.isOwner ?? false,
            tenantRoleId: data.data.user.tenantRoleId ?? null,
            tenantRole: data.data.user.tenantRole ?? null,
            permissions: data.data.user.permissions ?? []
          });
          setTenant({
            id: data.data.tenant.id,
            name: data.data.tenant.name,
            slug: data.data.tenant.slug,
            industry: data.data.tenant.industry ?? null,
            businessType: data.data.tenant.businessType ?? null,
            businessId: data.data.tenant.businessId ?? null,
            businessWebsite: data.data.tenant.businessWebsite ?? null,
            billingName: data.data.tenant.billingName ?? null,
            billingStreet1: data.data.tenant.billingStreet1 ?? null,
            billingCity: data.data.tenant.billingCity ?? null,
            billingProvince: data.data.tenant.billingProvince ?? null,
            billingPostalCode: data.data.tenant.billingPostalCode ?? null,
            billingCountry: data.data.tenant.billingCountry ?? null,
            plan: data.data.tenant.plan,
            needsSetup: data.data.tenant.needsSetup ?? false,
            cardOnFile: data.data.tenant.cardOnFile ?? false,
            trialStatus: data.data.tenant.trialStatus ?? "none",
            trialEndsAt: data.data.tenant.trialEndsAt ?? null
          });
          return true;
        }
      }
      return false;
    } catch {
      return false;
    }
  }, [apiRequest, authPathPrefix]);
  const login = useCallback(
    async (email, password) => {
      setError(null);
      setIsLoading(true);
      try {
        const recaptchaToken = await executeRecaptcha("LOGIN");
        const response = await fetch(`${apiUrl}${authPathPrefix}/login`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email, password, recaptchaToken })
        });
        const data = await response.json();
        if (!response.ok || !data.success) {
          const errorMessage = data.error?.message || "Login failed";
          const errorCode = data.error?.code;
          setError(errorMessage);
          setIsLoading(false);
          return { success: false, error: errorMessage, errorCode };
        }
        accessTokenRef.current = data.data.accessToken;
        storeTokens(data.data.accessToken, data.data.refreshToken);
        setUser({
          id: data.data.user.id,
          email: data.data.user.email,
          firstName: data.data.user.firstName,
          lastName: data.data.user.lastName,
          role: data.data.user.role,
          tenantId: data.data.tenant.id,
          emailVerified: data.data.user.emailVerified,
          isOwner: data.data.user.isOwner ?? false,
          tenantRoleId: data.data.user.tenantRoleId ?? null,
          tenantRole: data.data.user.tenantRole ?? null,
          permissions: data.data.user.permissions ?? []
        });
        setTenant({
          id: data.data.tenant.id,
          name: data.data.tenant.name,
          slug: data.data.tenant.slug,
          industry: data.data.tenant.industry ?? null,
          businessType: data.data.tenant.businessType ?? null,
          businessId: data.data.tenant.businessId ?? null,
          businessWebsite: data.data.tenant.businessWebsite ?? null,
          billingName: data.data.tenant.billingName ?? null,
          billingStreet1: data.data.tenant.billingStreet1 ?? null,
          billingCity: data.data.tenant.billingCity ?? null,
          billingProvince: data.data.tenant.billingProvince ?? null,
          billingPostalCode: data.data.tenant.billingPostalCode ?? null,
          billingCountry: data.data.tenant.billingCountry ?? null,
          plan: data.data.tenant.plan,
          needsSetup: data.data.tenant.needsSetup ?? false,
          cardOnFile: data.data.tenant.cardOnFile ?? false,
          trialStatus: data.data.tenant.trialStatus ?? "none",
          trialEndsAt: data.data.tenant.trialEndsAt ?? null
        });
        setIsLoading(false);
        return { success: true, needsSetup: data.data.tenant.needsSetup ?? false };
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : "Network error";
        setError(errorMessage);
        setIsLoading(false);
        return { success: false, error: errorMessage };
      }
    },
    [apiUrl, authPathPrefix]
  );
  const signup = useCallback(
    async (signupData) => {
      setError(null);
      setIsLoading(true);
      try {
        const recaptchaToken = await executeRecaptcha("SIGNUP");
        const response = await fetch(`${apiUrl}${authPathPrefix}/signup`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ ...signupData, recaptchaToken })
        });
        const data = await response.json();
        if (!response.ok || !data.success) {
          const errorMessage = data.error?.message || "Signup failed";
          const errorCode = data.error?.code;
          setError(errorMessage);
          setIsLoading(false);
          return { success: false, error: errorMessage, errorCode };
        }
        accessTokenRef.current = data.data.accessToken;
        storeTokens(data.data.accessToken, data.data.refreshToken);
        setUser({
          id: data.data.user.id,
          email: data.data.user.email,
          firstName: data.data.user.firstName,
          lastName: data.data.user.lastName,
          role: data.data.user.role,
          tenantId: data.data.tenant.id,
          emailVerified: data.data.user.emailVerified,
          isOwner: data.data.user.isOwner ?? false,
          tenantRoleId: data.data.user.tenantRoleId ?? null,
          tenantRole: data.data.user.tenantRole ?? null,
          permissions: data.data.user.permissions ?? []
        });
        setTenant({
          id: data.data.tenant.id,
          name: data.data.tenant.name,
          slug: data.data.tenant.slug,
          industry: data.data.tenant.industry ?? null,
          businessType: data.data.tenant.businessType ?? null,
          businessId: data.data.tenant.businessId ?? null,
          businessWebsite: data.data.tenant.businessWebsite ?? null,
          billingName: data.data.tenant.billingName ?? null,
          billingStreet1: data.data.tenant.billingStreet1 ?? null,
          billingCity: data.data.tenant.billingCity ?? null,
          billingProvince: data.data.tenant.billingProvince ?? null,
          billingPostalCode: data.data.tenant.billingPostalCode ?? null,
          billingCountry: data.data.tenant.billingCountry ?? null,
          plan: data.data.tenant.plan,
          needsSetup: data.data.tenant.needsSetup ?? false,
          cardOnFile: data.data.tenant.cardOnFile ?? false,
          trialStatus: data.data.tenant.trialStatus ?? "none",
          trialEndsAt: data.data.tenant.trialEndsAt ?? null
        });
        setIsLoading(false);
        return { success: true };
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : "Network error";
        setError(errorMessage);
        setIsLoading(false);
        return { success: false, error: errorMessage };
      }
    },
    [apiUrl, authPathPrefix]
  );
  const logout = useCallback(async () => {
    const tokens = getStoredTokens();
    try {
      await fetch(`${apiUrl}${authPathPrefix}/logout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refreshToken: tokens.refreshToken })
      });
    } catch {
    }
    clearTokens();
    accessTokenRef.current = null;
    setUser(null);
    setTenant(null);
    setError(null);
  }, [apiUrl, authPathPrefix]);
  const apiClientInitializedRef = useRef(false);
  if (!apiClientInitializedRef.current) {
    initializeApiClient({
      baseUrl: apiUrl,
      getAccessToken,
      onUnauthorized: () => {
        void logout();
      }
    });
    apiClientInitializedRef.current = true;
  }
  const forgotPassword = useCallback(
    async (email) => {
      try {
        const recaptchaToken = await executeRecaptcha("FORGOT_PASSWORD");
        const response = await fetch(`${apiUrl}${authPathPrefix}/forgot-password`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email, recaptchaToken })
        });
        const data = await response.json();
        if (!response.ok || !data.success) {
          return { success: false, error: data.error?.message || "Failed to send reset email" };
        }
        return { success: true };
      } catch (err) {
        return { success: false, error: err instanceof Error ? err.message : "Network error" };
      }
    },
    [apiUrl, authPathPrefix]
  );
  const resetPassword = useCallback(
    async (token, password) => {
      try {
        const response = await fetch(`${apiUrl}${authPathPrefix}/reset-password`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ token, password })
        });
        const data = await response.json();
        if (!response.ok || !data.success) {
          return { success: false, error: data.error?.message || "Failed to reset password" };
        }
        return { success: true };
      } catch (err) {
        return { success: false, error: err instanceof Error ? err.message : "Network error" };
      }
    },
    [apiUrl, authPathPrefix]
  );
  const verifyEmail = useCallback(
    async (token) => {
      try {
        const response = await fetch(`${apiUrl}${authPathPrefix}/verify-email`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ token })
        });
        const data = await response.json();
        if (!response.ok || !data.success) {
          return { success: false, error: data.error?.message || "Failed to verify email" };
        }
        setUser((prev) => prev ? { ...prev, emailVerified: true } : prev);
        return { success: true };
      } catch (err) {
        return { success: false, error: err instanceof Error ? err.message : "Network error" };
      }
    },
    [apiUrl, authPathPrefix]
  );
  const resendVerification = useCallback(
    async (email) => {
      try {
        const recaptchaToken = await executeRecaptcha("RESEND_VERIFICATION");
        const response = await fetch(`${apiUrl}${authPathPrefix}/resend-verification`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email, recaptchaToken })
        });
        const data = await response.json();
        if (!response.ok || !data.success) {
          return { success: false, error: data.error?.message || "Failed to resend verification" };
        }
        return { success: true, alreadyVerified: data.data?.alreadyVerified === true };
      } catch (err) {
        return { success: false, error: err instanceof Error ? err.message : "Network error" };
      }
    },
    [apiUrl, authPathPrefix]
  );
  const loginWithGoogle = useCallback(() => {
    window.location.href = `${apiUrl}${authPathPrefix}/oauth/google`;
  }, [apiUrl, authPathPrefix]);
  const loginWithMicrosoft = useCallback(() => {
    window.location.href = `${apiUrl}${authPathPrefix}/oauth/microsoft`;
  }, [apiUrl, authPathPrefix]);
  const loginWithApple = useCallback(() => {
    window.location.href = `${apiUrl}${authPathPrefix}/oauth/apple`;
  }, [apiUrl, authPathPrefix]);
  const linkAccount = useCallback(
    async (token, password) => {
      setError(null);
      try {
        const response = await fetch(`${apiUrl}${authPathPrefix}/oauth/link-account`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ token, password })
        });
        const data = await response.json();
        if (!response.ok || !data.success) {
          const errorMessage = data.error?.message || "Failed to link account";
          setError(errorMessage);
          return { success: false, error: errorMessage };
        }
        accessTokenRef.current = data.data.accessToken;
        storeTokens(data.data.accessToken, data.data.refreshToken);
        await fetchUserProfile();
        return { success: true };
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : "Network error";
        setError(errorMessage);
        return { success: false, error: errorMessage };
      }
    },
    [apiUrl, fetchUserProfile, authPathPrefix]
  );
  const handleOAuthCallback = useCallback(
    async (accessToken, refreshTokenValue) => {
      setError(null);
      setIsLoading(true);
      try {
        accessTokenRef.current = accessToken;
        storeTokens(accessToken, refreshTokenValue);
        const success = await fetchUserProfile();
        if (!success) {
          clearTokens();
          accessTokenRef.current = null;
          setIsLoading(false);
          return { success: false, error: "Failed to fetch user profile" };
        }
        setIsLoading(false);
        return { success: true };
      } catch (err) {
        clearTokens();
        accessTokenRef.current = null;
        const errorMessage = err instanceof Error ? err.message : "Network error";
        setError(errorMessage);
        setIsLoading(false);
        return { success: false, error: errorMessage };
      }
    },
    [fetchUserProfile]
  );
  const oauthSignup = useCallback(
    async (signupData) => {
      setError(null);
      setIsLoading(true);
      try {
        const response = await fetch(`${apiUrl}${authPathPrefix}/oauth/oauth-signup`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(signupData)
        });
        const data = await response.json();
        if (!response.ok || !data.success) {
          const errorMessage = data.error?.message || "OAuth signup failed";
          setError(errorMessage);
          setIsLoading(false);
          return { success: false, error: errorMessage };
        }
        accessTokenRef.current = data.data.accessToken;
        storeTokens(data.data.accessToken, data.data.refreshToken);
        const profileSuccess = await fetchUserProfile();
        if (!profileSuccess) {
          clearTokens();
          accessTokenRef.current = null;
          setIsLoading(false);
          return { success: false, error: "Failed to fetch user profile after signup" };
        }
        setIsLoading(false);
        return { success: true };
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : "Network error";
        setError(errorMessage);
        setIsLoading(false);
        return { success: false, error: errorMessage };
      }
    },
    [apiUrl, authPathPrefix, fetchUserProfile]
  );
  const getLoginMethods = useCallback(async () => {
    try {
      const response = await apiRequest(`${authPathPrefix}/oauth/login-methods`, { method: "GET" });
      const data = await response.json();
      if (!response.ok || !data.success) {
        return { success: false, error: data.error?.message || "Failed to fetch login methods" };
      }
      return { success: true, data: data.data };
    } catch (err) {
      return { success: false, error: err instanceof Error ? err.message : "Network error" };
    }
  }, [apiRequest, authPathPrefix]);
  const unlinkAccount = useCallback(
    async (linkedAccountId) => {
      try {
        const response = await apiRequest(`${authPathPrefix}/oauth/linked-accounts/${linkedAccountId}`, {
          method: "DELETE"
        });
        const data = await response.json();
        if (!response.ok || !data.success) {
          return { success: false, error: data.error?.message || "Failed to unlink account" };
        }
        return { success: true };
      } catch (err) {
        return { success: false, error: err instanceof Error ? err.message : "Network error" };
      }
    },
    [apiRequest, authPathPrefix]
  );
  const changePassword = useCallback(
    async (currentPassword, newPassword) => {
      try {
        const response = await apiRequest(`${authPathPrefix}/change-password`, {
          method: "POST",
          body: JSON.stringify({ currentPassword, newPassword })
        });
        const data = await response.json();
        if (!response.ok || !data.success) {
          return { success: false, error: data.error?.message || "Failed to change password" };
        }
        return { success: true };
      } catch (err) {
        return { success: false, error: err instanceof Error ? err.message : "Network error" };
      }
    },
    [apiRequest, authPathPrefix]
  );
  useEffect(() => {
    const initializeAuth = async () => {
      migrateLegacyTokens();
      const tokens = getStoredTokens();
      if (!tokens.accessToken) {
        setIsInitializing(false);
        return;
      }
      if (isTokenExpired(tokens.accessToken)) {
        const refreshed = await refreshToken();
        if (!refreshed) {
          setIsInitializing(false);
          return;
        }
      } else {
        accessTokenRef.current = tokens.accessToken;
      }
      const success = await fetchUserProfile();
      if (!success) {
        clearTokens();
        accessTokenRef.current = null;
      }
      setIsInitializing(false);
    };
    initializeAuth();
  }, [refreshToken, fetchUserProfile]);
  const contextValue = {
    user,
    tenant,
    isAuthenticated: !!user,
    isLoading,
    error,
    login,
    signup,
    logout,
    getAccessToken,
    refreshToken,
    refreshUser: fetchUserProfile,
    forgotPassword,
    resetPassword,
    verifyEmail,
    resendVerification,
    loginWithGoogle,
    loginWithMicrosoft,
    loginWithApple,
    linkAccount,
    handleOAuthCallback,
    oauthSignup,
    getLoginMethods,
    unlinkAccount,
    changePassword
  };
  if (isInitializing) {
    if (loadingComponent) {
      return /* @__PURE__ */ jsx(Fragment, { children: loadingComponent });
    }
    return /* @__PURE__ */ jsxs("div", { style: { display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }, children: [
      /* @__PURE__ */ jsx(
        "div",
        {
          style: {
            width: "40px",
            height: "40px",
            border: "4px solid #e5e5e5",
            borderTop: "4px solid #3b82f6",
            borderRadius: "50%",
            animation: "spin 1s linear infinite"
          }
        }
      ),
      /* @__PURE__ */ jsx("style", { children: `
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        ` })
    ] });
  }
  return /* @__PURE__ */ jsx(AuthContext.Provider, { value: contextValue, children });
}
function withAuthProvider(WrappedComponent, apiUrl) {
  return function WithAuthProvider(props) {
    return /* @__PURE__ */ jsx(AuthProvider, { apiUrl, children: /* @__PURE__ */ jsx(WrappedComponent, { ...props }) });
  };
}
function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return {
    isAuthenticated: context.isAuthenticated,
    isLoading: context.isLoading,
    error: context.error,
    user: context.user,
    tenant: context.tenant,
    login: context.login,
    signup: context.signup,
    logout: context.logout,
    getAccessToken: context.getAccessToken,
    refreshToken: context.refreshToken,
    refreshUser: context.refreshUser,
    forgotPassword: context.forgotPassword,
    resetPassword: context.resetPassword,
    verifyEmail: context.verifyEmail,
    resendVerification: context.resendVerification,
    loginWithGoogle: context.loginWithGoogle,
    loginWithMicrosoft: context.loginWithMicrosoft,
    loginWithApple: context.loginWithApple,
    linkAccount: context.linkAccount,
    handleOAuthCallback: context.handleOAuthCallback,
    oauthSignup: context.oauthSignup,
    getLoginMethods: context.getLoginMethods,
    unlinkAccount: context.unlinkAccount,
    changePassword: context.changePassword
  };
}
function useIsAuthenticated() {
  const context = useContext(AuthContext);
  return context?.isAuthenticated ?? false;
}
function useCurrentUser() {
  const context = useContext(AuthContext);
  return context?.user ?? null;
}
function useCurrentTenant() {
  const context = useContext(AuthContext);
  return context?.tenant ?? null;
}
function DefaultLoading() {
  return /* @__PURE__ */ jsxs(
    "div",
    {
      style: {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100vh",
        flexDirection: "column",
        gap: "1rem"
      },
      children: [
        /* @__PURE__ */ jsx(
          "div",
          {
            style: {
              width: "40px",
              height: "40px",
              border: "4px solid #e5e5e5",
              borderTop: "4px solid #3b82f6",
              borderRadius: "50%",
              animation: "spin 1s linear infinite"
            }
          }
        ),
        /* @__PURE__ */ jsx("style", { children: `
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      ` }),
        /* @__PURE__ */ jsx("p", { style: { color: "#6b7280", fontSize: "0.875rem" }, children: "Loading..." })
      ]
    }
  );
}
function DefaultUnauthorized() {
  return /* @__PURE__ */ jsxs(
    "div",
    {
      style: {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100vh",
        flexDirection: "column",
        gap: "1rem",
        padding: "2rem",
        textAlign: "center"
      },
      children: [
        /* @__PURE__ */ jsx(
          "svg",
          {
            style: { width: "64px", height: "64px", color: "#ef4444" },
            fill: "none",
            stroke: "currentColor",
            viewBox: "0 0 24 24",
            children: /* @__PURE__ */ jsx(
              "path",
              {
                strokeLinecap: "round",
                strokeLinejoin: "round",
                strokeWidth: 2,
                d: "M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              }
            )
          }
        ),
        /* @__PURE__ */ jsx("h2", { style: { fontSize: "1.5rem", fontWeight: "bold", color: "#1f2937" }, children: "Access Denied" }),
        /* @__PURE__ */ jsx("p", { style: { color: "#6b7280", maxWidth: "400px" }, children: "You don't have permission to access this page. Please contact your administrator if you believe this is an error." })
      ]
    }
  );
}
function DefaultUnauthenticated() {
  return /* @__PURE__ */ jsxs(
    "div",
    {
      style: {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100vh",
        flexDirection: "column",
        gap: "1rem"
      },
      children: [
        /* @__PURE__ */ jsx(
          "div",
          {
            style: {
              width: "32px",
              height: "32px",
              border: "3px solid #e5e5e5",
              borderTop: "3px solid #3b82f6",
              borderRadius: "50%",
              animation: "spin 1s linear infinite"
            }
          }
        ),
        /* @__PURE__ */ jsx("p", { style: { color: "#6b7280" }, children: "Redirecting to sign in..." })
      ]
    }
  );
}
function ProtectedRoute({
  children,
  requiredRoles,
  requiredPermission,
  requiredAnyPermission,
  unauthorizedRedirect = "/",
  loadingComponent,
  unauthorizedComponent,
  unauthenticatedComponent,
  loginPath = "/login",
  onUnauthorized
}) {
  const { isAuthenticated, user, isLoading } = useAuth();
  const permissionAllowed = useMemo(() => {
    if (!user) return false;
    const perms = new Set(user.permissions ?? []);
    if (requiredPermission && !perms.has(requiredPermission)) return false;
    if (requiredAnyPermission && requiredAnyPermission.length > 0) {
      if (!requiredAnyPermission.some((p) => perms.has(p))) return false;
    }
    return true;
  }, [user?.permissions, requiredPermission, requiredAnyPermission?.join(",")]);
  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      const currentPath = window.location.pathname + window.location.search;
      if (currentPath !== loginPath) {
        sessionStorage.setItem("auth_redirect_path", currentPath);
      }
      window.location.href = loginPath;
    }
  }, [isAuthenticated, isLoading, loginPath]);
  useEffect(() => {
    if (!isLoading && isAuthenticated && user && !permissionAllowed) {
      if (onUnauthorized) onUnauthorized();
      if (window.location.pathname !== unauthorizedRedirect) {
        window.location.href = unauthorizedRedirect;
      }
    }
  }, [isLoading, isAuthenticated, user, permissionAllowed, unauthorizedRedirect, onUnauthorized]);
  if (isLoading) {
    return /* @__PURE__ */ jsx(Fragment, { children: loadingComponent || /* @__PURE__ */ jsx(DefaultLoading, {}) });
  }
  if (!isAuthenticated) {
    return /* @__PURE__ */ jsx(Fragment, { children: unauthenticatedComponent || /* @__PURE__ */ jsx(DefaultUnauthenticated, {}) });
  }
  if (requiredRoles && requiredRoles.length > 0 && user) {
    const hasRequiredRole = requiredRoles.some(
      (role) => role.toLowerCase() === user.role?.toLowerCase()
    );
    if (!hasRequiredRole) {
      if (onUnauthorized) onUnauthorized();
      return /* @__PURE__ */ jsx(Fragment, { children: unauthorizedComponent || /* @__PURE__ */ jsx(DefaultUnauthorized, {}) });
    }
  }
  if (!permissionAllowed) {
    return /* @__PURE__ */ jsx(Fragment, { children: loadingComponent || /* @__PURE__ */ jsx(DefaultLoading, {}) });
  }
  return /* @__PURE__ */ jsx(Fragment, { children });
}
function withProtectedRoute(WrappedComponent, options) {
  return function ProtectedRouteWrapper(props) {
    return /* @__PURE__ */ jsx(ProtectedRoute, { ...options, children: /* @__PURE__ */ jsx(WrappedComponent, { ...props }) });
  };
}
function usePermission(permission) {
  const { user } = useAuth();
  return useMemo(
    () => user?.permissions ? new Set(user.permissions).has(permission) : false,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user?.permissions, permission]
  );
}
function useAllPermissions(permissions) {
  const { user } = useAuth();
  return useMemo(() => {
    if (!user?.permissions) return false;
    const set = new Set(user.permissions);
    return permissions.every((p) => set.has(p));
  }, [user?.permissions, permissions.join(",")]);
}
function useAnyPermission(permissions) {
  const { user } = useAuth();
  return useMemo(() => {
    if (!user?.permissions) return false;
    const set = new Set(user.permissions);
    return permissions.some((p) => set.has(p));
  }, [user?.permissions, permissions.join(",")]);
}
function useIsOwner() {
  const { user } = useAuth();
  return user?.isOwner === true;
}
function usePermissions() {
  const { user } = useAuth();
  return user?.permissions ?? [];
}
function PermissionGuard({
  permission,
  allOf,
  anyOf,
  ownerOnly,
  fallback = null,
  children
}) {
  const hasSingle = usePermission(permission ?? "");
  const hasAll = useAllPermissions(allOf ?? []);
  const hasAny = useAnyPermission(anyOf ?? []);
  const isOwner = useIsOwner();
  let allowed = true;
  if (ownerOnly) allowed = allowed && isOwner;
  if (permission != null) allowed = allowed && hasSingle;
  if (allOf != null) allowed = allowed && hasAll;
  if (anyOf != null) allowed = allowed && hasAny;
  return /* @__PURE__ */ jsx(Fragment, { children: allowed ? children : fallback });
}

export { AuthContext, AuthProvider, PermissionGuard, ProtectedRoute, isTrialActive, useAllPermissions, useAnyPermission, useAuth, useCurrentTenant, useCurrentUser, useIsAuthenticated, useIsOwner, usePermission, usePermissions, withAuthProvider, withProtectedRoute };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map